import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';
import { Icons } from 'components/Icons';
import Logo from 'components/Logo';
import { STANDARD_LOGO_CONFIG } from '../Header/Header';
import { useTranslation } from 'utils/i18n';
import sharedTranslations from 'components/shared/translations';
import { isOnPlatformAuth, getPublicPageUrl } from 'utils/getPublicDomain';

const Footer = () => {
  const { t: tCommon, language } = useTranslation();

  // Check if we're on platform-auth - if so, use absolute URLs pointing to platform-public
  const isAuthPortal = isOnPlatformAuth();
  
  
  // Get shared component translations
  const getSharedTranslation = (key, defaultValue = '') => {
    const translations = sharedTranslations[language] || sharedTranslations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], translations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    // Fallback to common translations
    const commonValue = tCommon(key);
    if (commonValue !== key && commonValue !== undefined) {
      return commonValue;
    }
    return defaultValue !== '' ? defaultValue : key;
  };
  
  const t = (key, defaultValue = '') => {
    // Try shared translations first
    const sharedValue = getSharedTranslation(key, '');
    if (sharedValue !== key && sharedValue !== '') {
      return sharedValue;
    }
    // Fallback to common translations
    return tCommon(key, defaultValue);
  };
  const currentYear = new Date().getFullYear();
  const [showAmdgTooltip, setShowAmdgTooltip] = useState(false);
  
  const handleAmdgClick = () => {
    setShowAmdgTooltip(!showAmdgTooltip);
  };
  
  const handleAmdgBlur = () => {
    // Delay hiding to allow clicking on tooltip
    setTimeout(() => setShowAmdgTooltip(false), 150);
  };
  
  // Scroll to top when footer link is clicked
  const handleFooterLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper to render upcoming links consistently
  const renderUpcomingLink = (text, badgeText = 'Upcoming') => (
    <span className="footer-link upcoming-link">
      {text}
      <span className="upcoming-badge">{badgeText}</span>
    </span>
  );

  // Footer links: same tab (SPA Link on current app; full navigation to public when on auth unauthenticated)
  const renderFooterLink = (path, children, className = 'footer-link', isAuthenticated = false) => {
    const linkSameApp = (
      <Link to={path} className={className} onClick={handleFooterLinkClick}>
        {children}
      </Link>
    );
    if (isAuthenticated) {
      return linkSameApp;
    }
    if (isAuthPortal) {
      const href = getPublicPageUrl(path);
      return (
        <a href={href} className={className} onClick={handleFooterLinkClick}>
          {children}
        </a>
      );
    }
    return linkSameApp;
  };
  
  return (
    <footer className="footer">
        <div className="footer-container">
        <div className="footer-content">
          {/* Company Info */}
          <div className="footer-section">
            <div className="footer-logo">
              <Logo {...STANDARD_LOGO_CONFIG} reversed={true} showTagline={false} showVerticalLine={false} />
            </div>
            <p className="footer-description">
              {t('footer.description')}
            </p>
            <div className="footer-contact">
              <div className="contact-item">
                <Icons.Mail size={16} />
                <span>info@b-esper.com</span>
              </div>
              <div className="contact-item">
                <Icons.Phone size={16} />
                <span>+49 171 67 24 126</span>
              </div>
            </div>

          </div>

          {/* Product Links */}
          <div className="footer-section">
            <h3>{t('footer.product.title')}</h3>
            <ul className="footer-links">
              <li>{renderFooterLink('/about', t('footer.product.about'))}</li>
              <li>{renderFooterLink('/roadmap', t('footer.product.roadmap'))}</li>
              <li>{renderFooterLink('/docs', t('footer.product.documentation'))}</li>
              <li>{renderUpcomingLink(t('footer.product.sla', 'Service Level Agreements'), 'Soon')}</li>
              <li>{renderUpcomingLink(t('footer.product.changelog'), 'Upcoming')}</li>
              <li>{renderUpcomingLink(t('footer.product.partners'), 'Upcoming')}</li>
              <li>{renderFooterLink('/marketplace', t('footer.product.marketplace'), 'footer-link', true)}</li>
              <li>{renderFooterLink('/besper-ai', t('footer.product.besperAi', 'Besper AI'))}</li>
              <li>{renderUpcomingLink(t('footer.company.besperAiLab'), 'Upcoming')}</li>
              <li>{renderUpcomingLink(t('footer.product.testimonials'), 'Upcoming')}</li>
            </ul>
          </div>

          {/* Support Links */}
          <div className="footer-section">
            <h3>{t('footer.support.title')}</h3>
            <ul className="footer-links">
              <li>{renderUpcomingLink(t('footer.support.faq', 'FAQ'), 'Soon')}</li>
              <li>{renderUpcomingLink(t('footer.support.developerDocs', 'Developer Docs'), 'Soon')}</li>
              <li>{renderUpcomingLink(t('footer.support.bugReports', 'Bug Reports'), 'Soon')}</li>
              <li>{renderUpcomingLink(t('footer.support.contact', 'Contact'), 'Soon')}</li>
              <li>{renderUpcomingLink(t('footer.support.helpCenter', 'Help Center'), 'Soon')}</li>
              <li>{renderUpcomingLink(t('footer.support.community', 'Community'), 'Soon')}</li>
              <li>{renderUpcomingLink(t('footer.support.becomePartner', 'Become Partner'), 'Soon')}</li>
              <li>{renderUpcomingLink(t('footer.support.feedback', 'Feedback'), 'Soon')}</li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="footer-section">
            <h3>{t('footer.legal.title')}</h3>
            <ul className="footer-links">
              <li>{renderFooterLink('/terms', t('footer.legal.terms'))}</li>
              <li>{renderFooterLink('/privacy', t('footer.legal.privacy'))}</li>
              <li>{renderFooterLink('/eula', t('footer.legal.eula'))}</li>
              <li>{renderFooterLink('/acceptable-use', t('footer.legal.acceptableUse'))}</li>
              <li>{renderFooterLink('/disclaimer', t('footer.legal.disclaimer'))}</li>
              <li>{renderFooterLink('/impressum', t('footer.legal.imprint'))}</li>
              <li>{renderFooterLink('/cookie-settings', t('footer.legal.cookieSettings'))}</li>
              <li>{renderFooterLink('/gdpr', t('footer.legal.gdpr'))}</li>
              <li>{renderFooterLink('/dpa', t('footer.legal.dpa'))}</li>
            </ul>
          </div>

          {/* Company Links */}
          <div className="footer-section">
            <h3>{t('footer.company.title')}</h3>
            <ul className="footer-links">
              <li>{renderFooterLink('/architecture', t('footer.company.architecture'))}</li>
              <li>{renderFooterLink('/strategy', t('footer.company.ourStrategy'))}</li>
              <li>{renderFooterLink('/besper-ai', t('footer.product.besperAi', 'Besper AI'))}</li>
              <li>{renderUpcomingLink(t('footer.company.besperAiLab'), 'Upcoming')}</li>
              <li>{renderUpcomingLink(t('footer.company.events'), 'Upcoming')}</li>
              <li>{renderUpcomingLink(t('footer.company.press'), 'Upcoming')}</li>
              <li>{renderUpcomingLink(t('footer.company.investors'), 'Upcoming')}</li>
              <li>{renderUpcomingLink(t('footer.company.newsletter'), 'Upcoming')}</li>
            </ul>
          </div>
        </div>

        {/* Enhanced Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <div className="footer-left">
              <p>{t('footer.bottom.copyright', `© ${currentYear} B-esper GmbH. All rights reserved.`).replace('{year}', currentYear)}</p>
              <div className="footer-address">
                <p>{t('footer.bottom.address')}</p>
              </div>
            </div>
            <div className="footer-right">
              <div className="footer-meta">
                <span>{t('footer.bottom.madeInGermany', 'Made in Germany')}</span>
                <span>•</span>
                <span>{t('footer.bottom.gdprCompliant', 'GDPR Compliant')}</span>
                <span>•</span>
                <span>{t('footer.bottom.euHosting', 'EU Hosting')}</span>
                <span>•</span>
                {/* A.M.D.G. with tooltip */}
                <div className="footer-amdg-container">
                  <button 
                    className="footer-amdg"
                    onClick={handleAmdgClick}
                    onBlur={handleAmdgBlur}
                    title={t('footer.bottom.amdg.tooltip.title', 'Click to learn more')}
                    aria-label={t('footer.bottom.amdg.tooltip.ariaLabel', 'A.M.D.G. - Click to learn more')}
                  >
                    A.M.D.G.
                  </button>
                  {showAmdgTooltip && (
                    <div className="amdg-tooltip">
                      <div className="amdg-tooltip-content">
                        <strong>{t('footer.bottom.amdg.tooltip.latin', 'Ad Majorem Dei Gloriam')}</strong>
                        <p>{t('footer.bottom.amdg.tooltip.translation', 'For the greater glory of God')}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="footer-certifications">
                <span className="cert-badge">{t('footer.bottom.gdprBadge', 'GDPR Compliant')}</span>
              </div>
              <div className="footer-social">
                <a href="https://x.com/Besper_official" className="social-link footer-link" aria-label={t('footer.bottom.socialMedia.twitter', 'Follow us on Twitter')} target="_blank" rel="noopener noreferrer">
                  <Icons.Twitter size={18} />
                </a>
                <a href="https://www.linkedin.com/company/106351579" className="social-link footer-link" aria-label={t('footer.bottom.socialMedia.linkedin', 'Follow us on LinkedIn')} target="_blank" rel="noopener noreferrer">
                  <Icons.LinkedIn size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;